import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

declare var RTA_API: string;
declare var CURRENT_USER_ID: number;

export interface UserNotificationPreference {
  id?: number;
  userId: number;
  eventType: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  createdAt?: Date;
  modifiedAt?: Date;
}

export interface EventTypeInfo {
  eventType: string;
  displayName: string;
  description: string;
  category: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationPreferencesService {
  private baseUrl: string = RTA_API.replace('/RTA', '/Notification');
  private currentUserId: number = CURRENT_USER_ID;

  constructor(private http: HttpClient) { }

  getCurrentUserPreferences(): Observable<UserNotificationPreference[]> {
    return this.http.get<UserNotificationPreference[]>(`${this.baseUrl}/preferences/${this.currentUserId}`);
  }

  getUserPreferences(userId: number): Observable<UserNotificationPreference[]> {
    return this.http.get<UserNotificationPreference[]>(`${this.baseUrl}/preferences/${userId}`);
  }

  updatePreference(preference: UserNotificationPreference): Observable<UserNotificationPreference> {
    return this.http.put<UserNotificationPreference>(`${this.baseUrl}/preferences`, preference);
  }

  subscribe(eventType: string, userId?: number): Observable<any> {
    const uid = userId || this.currentUserId;
    return this.http.post(`${this.baseUrl}/preferences/subscribe/${eventType}?userId=${uid}`, {});
  }

  unsubscribe(eventType: string, userId?: number): Observable<any> {
    const uid = userId || this.currentUserId;
    return this.http.post(`${this.baseUrl}/preferences/unsubscribe/${eventType}?userId=${uid}`, {});
  }

  initializePreferences(userId?: number): Observable<any> {
    const uid = userId || this.currentUserId;
    return this.http.post(`${this.baseUrl}/preferences/initialize/${uid}`, {});
  }

  getEventTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/event-types`);
  }

  getEventTypeInfo(): EventTypeInfo[] {
    return [
      // Order Events
      { eventType: 'OrderCreated', displayName: 'Order Created', description: 'When a new order is created', category: 'Orders' },
      { eventType: 'OrderSubmitted', displayName: 'Order Submitted', description: 'When an order is submitted for review', category: 'Orders' },
      { eventType: 'OrderApproved', displayName: 'Order Approved', description: 'When an order is approved', category: 'Orders' },
      { eventType: 'OrderRejected', displayName: 'Order Rejected', description: 'When an order is rejected', category: 'Orders' },
      { eventType: 'OrderCompleted', displayName: 'Order Completed', description: 'When an order is completed', category: 'Orders' },

      // Task Events
      { eventType: 'TaskAssigned', displayName: 'Task Assigned', description: 'When a task is assigned to you', category: 'Tasks' },
      { eventType: 'TaskPickedUp', displayName: 'Task Picked Up', description: 'When someone picks up a task', category: 'Tasks' },
      { eventType: 'TaskStatusChanged', displayName: 'Task Status Changed', description: 'When a task status changes', category: 'Tasks' },

      // Override Events
      { eventType: 'CutterOverrideRequested', displayName: 'Override Requested', description: 'When a cutter override is requested', category: 'Overrides' },
      { eventType: 'CutterOverrideApproved', displayName: 'Override Approved', description: 'When a cutter override is approved', category: 'Overrides' },
      { eventType: 'CutterOverrideDenied', displayName: 'Override Denied', description: 'When a cutter override is denied', category: 'Overrides' },

      // Other Events
      { eventType: 'FileUploaded', displayName: 'File Uploaded', description: 'When a file is uploaded to an order', category: 'Other' },
      { eventType: 'CommentAdded', displayName: 'Comment Added', description: 'When a comment is added to an order', category: 'Other' },
      { eventType: 'PriorityChanged', displayName: 'Priority Changed', description: 'When an order priority changes', category: 'Other' }
    ];
  }

  getEventTypesByCategory(): Map<string, EventTypeInfo[]> {
    const eventTypes = this.getEventTypeInfo();
    const categories = new Map<string, EventTypeInfo[]>();

    eventTypes.forEach(et => {
      if (!categories.has(et.category)) {
        categories.set(et.category, []);
      }
      categories.get(et.category)!.push(et);
    });

    return categories;
  }
}
